import {
  getObject,
  getList,
  LeoUUID,
  getLeoUuid,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { EmbedName, EmbedNameDTO } from "./embedName";
import { EmbedCode, EmbedCodeDTO } from "./embedCode";
import { EmbedDetail, EmbedDetailDTO } from "./embedDetail";
/* eslint-disable import/export */
export abstract class AddNewEmbedRPC implements
  LeoRPC<AddNewEmbedRPC.Request, AddNewEmbedRPC.Response, AddNewEmbedRPC.Errors.Errors>
{
  abstract execute(
    request: AddNewEmbedRPC.Request
  ): Promise<LeoRPCResult<AddNewEmbedRPC.Response, AddNewEmbedRPC.Errors.Errors>>;
}

export namespace AddNewEmbedRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    embedName: EmbedNameDTO;
    embedCode: EmbedCodeDTO;
    selectedSPoCs: string[];
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isAddNewEmbedRPCRequest: boolean = true;
  
    readonly embedName: EmbedName;
    readonly embedCode: EmbedCode;
    readonly selectedSPoCs: LeoUUID[];
    readonly projectId: LeoUUID;
  
  
    constructor(
    embedName: EmbedName, 
    embedCode: EmbedCode, 
    selectedSPoCs: LeoUUID[], 
    projectId: LeoUUID
    ) {
      this.embedName = embedName;
      this.embedCode = embedCode;
      this.selectedSPoCs = selectedSPoCs;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const embedName = EmbedName.fromDTO(getObject(dto, "embedName", InvalidRequestEmbedNameError));
      const embedCode = EmbedCode.fromDTO(getObject(dto, "embedCode", InvalidRequestEmbedCodeError));
      const selectedSPoCs = getList<string, LeoUUID>(
        dto,
        "selectedSPoCs",
        (e: string) => new LeoUUID(e),
        InvalidRequestSelectedSPoCsError
      );
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        embedName,
        embedCode,
        selectedSPoCs,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          embedName: this.embedName.toDTO(),
          embedCode: this.embedCode.toDTO(),
          selectedSPoCs: this.selectedSPoCs.map((e: LeoUUID) => { return e.toDTO(); }),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `AddNewEmbedRPC(embedName='${this.embedName}',embedCode='${this.embedCode}',selectedSPoCs='${this.selectedSPoCs}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    embedDetails: EmbedDetailDTO[];
  }
  
  export class Response implements LeoRPCResponse {
    readonly isAddNewEmbedResponse: boolean = true;
  
    readonly embedDetails: EmbedDetail[];
  
  
    constructor(
    embedDetails: EmbedDetail[]
    ) {
      this.embedDetails = embedDetails;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const embedDetails = getList<EmbedDetailDTO, EmbedDetail>(
        dto,
        "embedDetails",
        EmbedDetail.fromDTO,
        InvalidResponseEmbedDetailsError
      );
      return new Response(
        embedDetails
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          embedDetails: this.embedDetails.map((e: EmbedDetail) => { return e.toDTO() })
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `AddNewEmbed(embedDetails='${this.embedDetails}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    EmbedNameNotUnique = "EMBED_NAME_NOT_UNIQUE",
    MaximumEmbedCountReached = "MAXIMUM_EMBED_COUNT_REACHED",
    InvalidSpocId = "INVALID_SPOC_ID",
    MaximumSpocPerEmbedReached = "MAXIMUM_SPOC_PER_EMBED_REACHED",
    InvalidEmbedName = "INVALID_EMBED_NAME"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface EmbedNameNotUniqueDTO {
      code: string;
    }
    
    export class EmbedNameNotUnique extends LeoRPCError<RPCError> {
      readonly isEmbedNameNotUnique: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.EmbedNameNotUnique;
        super(code);
      }
    
    
      toDTO(): EmbedNameNotUniqueDTO {
        const dto: EmbedNameNotUniqueDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    EmbedNameNotUnique.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.EMBED_NAME_NOT_UNIQUE()`;
    };
    
    
    export interface MaximumEmbedCountReachedDTO {
      code: string;
    }
    
    export class MaximumEmbedCountReached extends LeoRPCError<RPCError> {
      readonly isMaximumEmbedCountReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumEmbedCountReached;
        super(code);
      }
    
    
      toDTO(): MaximumEmbedCountReachedDTO {
        const dto: MaximumEmbedCountReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumEmbedCountReached.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.MAXIMUM_EMBED_COUNT_REACHED()`;
    };
    
    
    export interface InvalidSpocIdDTO {
      code: string;
    }
    
    export class InvalidSpocId extends LeoRPCError<RPCError> {
      readonly isInvalidSpocId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSpocId;
        super(code);
      }
    
    
      toDTO(): InvalidSpocIdDTO {
        const dto: InvalidSpocIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSpocId.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.INVALID_SPOC_ID()`;
    };
    
    
    export interface MaximumSpocPerEmbedReachedDTO {
      code: string;
    }
    
    export class MaximumSpocPerEmbedReached extends LeoRPCError<RPCError> {
      readonly isMaximumSpocPerEmbedReached: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.MaximumSpocPerEmbedReached;
        super(code);
      }
    
    
      toDTO(): MaximumSpocPerEmbedReachedDTO {
        const dto: MaximumSpocPerEmbedReachedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    MaximumSpocPerEmbedReached.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.MAXIMUM_SPOC_PER_EMBED_REACHED()`;
    };
    
    
    export interface InvalidEmbedNameDTO {
      code: string;
    }
    
    export class InvalidEmbedName extends LeoRPCError<RPCError> {
      readonly isInvalidEmbedName: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidEmbedName;
        super(code);
      }
    
    
      toDTO(): InvalidEmbedNameDTO {
        const dto: InvalidEmbedNameDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidEmbedName.prototype.toString = function toString(): string {
      return `AddNewEmbedRPC.INVALID_EMBED_NAME()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | EmbedNameNotUnique | MaximumEmbedCountReached | InvalidSpocId | MaximumSpocPerEmbedReached | InvalidEmbedName;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | EmbedNameNotUniqueDTO | MaximumEmbedCountReachedDTO | InvalidSpocIdDTO | MaximumSpocPerEmbedReachedDTO | InvalidEmbedNameDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.EmbedNameNotUnique:
          return new EmbedNameNotUnique();
        case RPCError.MaximumEmbedCountReached:
          return new MaximumEmbedCountReached();
        case RPCError.InvalidSpocId:
          return new InvalidSpocId();
        case RPCError.MaximumSpocPerEmbedReached:
          return new MaximumSpocPerEmbedReached();
        case RPCError.InvalidEmbedName:
          return new InvalidEmbedName();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestEmbedNameError extends AddNewEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmbedNameError";
  }
}

export class InvalidRequestEmbedCodeError extends AddNewEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestEmbedCodeError";
  }
}

export class InvalidRequestSelectedSPoCsError extends AddNewEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSelectedSPoCsError";
  }
}

export class InvalidRequestProjectIdError extends AddNewEmbedRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidResponseEmbedDetailsError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmbedDetailsError";
  }
}

export class InvalidResponseInvalidProjectIdError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseEmbedNameNotUniqueError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseEmbedNameNotUniqueError";
  }
}

export class InvalidResponseMaximumEmbedCountReachedError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumEmbedCountReachedError";
  }
}

export class InvalidResponseInvalidSpocIdError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSpocIdError";
  }
}

export class InvalidResponseMaximumSpocPerEmbedReachedError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseMaximumSpocPerEmbedReachedError";
  }
}

export class InvalidResponseInvalidEmbedNameError extends AddNewEmbedRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidEmbedNameError";
  }
}

